import { Country } from '@afterpaytouch/portal-api/types'
import { Route } from '../config/router'
import { IdentityDocument, SupportedLocales } from '@afterpay/types'

export const AUS_STATES_LIST = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
} as const

export type AusStateKey = keyof typeof AUS_STATES_LIST
interface AusStatesList {
  key: AusStateKey
  value: (typeof AUS_STATES_LIST)[AusStateKey]
}

export interface CountryPathname {
  country?: string
}

const AustralianStatesList: AusStatesList[] = Object.keys(AUS_STATES_LIST)
  .filter((value): value is AusStateKey => isNaN(Number(value)))
  .map((key) => {
    return {
      key,
      value: AUS_STATES_LIST[key],
    }
  })

const COUNTRY_TO_STATES_LIST_MAP: Record<string, AusStatesList[] | []> = {
  AU: AustralianStatesList,
  NZ: [],
}

export const getStatesListByCountry = (country?: Country): AusStatesList[] | [] => {
  if (typeof country === 'undefined') {
    return []
  }

  return COUNTRY_TO_STATES_LIST_MAP[country]
}

export type Currency = 'AUD' | 'CAD' | 'GBP' | 'NZD' | 'USD' | 'EUR'
export type LegacyCurrency = 'EUR'

export type SupportedLocale = 'en-AU' | 'en-CA' | 'en-GB' | 'en-US' | 'en-NZ' | 'fr-CA' | 'fr-FR' | 'es-ES' | 'it-IT'
export type LegacyLocale = 'fr-FR' | 'es-ES' | 'it-IT'

export function parseCountry(query: string | string[] | undefined): Country {
  const key = typeof query === 'string' ? query?.toUpperCase() : Array.isArray(query) ? query[0]?.toUpperCase() : undefined
  if (key === 'UK') {
    return Country.GB
  }
  return Object.values(Country).find((country) => key === country) ?? Country.AU
}

export function isValidLocale(locale: string | undefined, country: Country): locale is SupportedLocale {
  return validateLocale(locale, country) === locale
}

export function validateLocale(locale: string | undefined, country: Country): SupportedLocale {
  switch (country) {
    case Country.AU:
      return 'en-AU'
    case Country.NZ:
      return 'en-NZ'
    case Country.GB:
      return 'en-GB'
    case Country.CA:
      return locale === 'fr-CA' ? 'fr-CA' : 'en-CA'
    case Country.US:
    default:
      return 'en-US'
  }
}

export interface CountryConfig {
  countryCode: Country
  currencyCode: Currency
  currencySymbol: string
  portalUrl: string
  // TODO: add checkoutUrl
}

export interface PortalCountries {
  countries: CountryConfig[]
}

export const mapLocaleToCurrency = (locale: SupportedLocale): Currency => {
  switch (locale) {
    case SupportedLocales.EN_AU:
      return 'AUD'
    case SupportedLocales.EN_NZ:
      return 'NZD'
    case SupportedLocales.EN_GB:
      return 'GBP'
    case SupportedLocales.EN_CA:
    case SupportedLocales.FR_CA:
      return 'CAD'
    case SupportedLocales.EN_US:
    default:
      return 'USD'
  }
}

export const isCountryNeedsBiometricVerification = (country: Country): boolean => [Country.GB, Country.CA].includes(country)

export const isCountryNeedsSoftIdAttempt = (country: Country): boolean => {
  return [Country.AU, Country.GB, Country.CA].includes(country)
}

export const isCountryHardIdvSkipDocumentSelection = (country: Country): boolean => [Country.CA].includes(country)

export const isCountrySkipCreateCheck = (country: Country): boolean => [Country.CA].includes(country)

export const isCountryUsePersonaFlow = (country: Country): boolean => [Country.CA].includes(country)

export interface GetVerifyDocumentRouteRequest {
  document: IdentityDocument
  retry?: boolean
  country: Country
}

export const getVerifyDocumentRoute = ({ document, retry = false, country }: GetVerifyDocumentRouteRequest): Route => {
  const needsBiometricVerification = isCountryNeedsBiometricVerification(country)
  if (needsBiometricVerification) {
    return `/verify-identity/biometric/${document}` as Route
  } else {
    return `/verify-identity/${retry ? 'retry/' : ''}${document}` as Route
  }
}
